
import { logout } from './auth';
import { apiCall } from './apiHelpers';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export async function authFetch(url, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };

  // Add /api prefix if not already present and not a full URL
  let fullUrl;
  if (url.startsWith('http')) {
    fullUrl = url;
  } else {
    // Add /api prefix if not already present
    const apiUrl = url.startsWith('/api/') ? url : `/api${url.startsWith('/') ? url : `/${url}`}`;
    fullUrl = `${API_BASE_URL}${apiUrl}`;
  }

  try {
    const response = await fetch(fullUrl, { ...options, headers });

    // Handle 401 Unauthorized response
    if (response.status === 401) {
      logout();
      throw new Error('Unauthorized access. Please login again.');
    }

    // Handle 403 Forbidden response (Subscription Limit/Expiry)
    if (response.status === 403) {
      // Parse error message if possible to confirm it's subscription related
      // For now, redirect to subscription page
      window.location.href = '/subscription';
      throw new Error('Subscription limit reached or trial expired.');
    }

    return response;
  } catch (error) {
    // Handle network errors or other fetch errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('Network error:', error);
      throw new Error('Network error. Please check your connection.');
    }
    throw error;
  }
}

// Report API functions
export const reportAPI = {
  async getTransactionReport(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(authFetch, `/report/transactions?${queryString}`);
  },

  async getCustomerReport(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(authFetch, `/report/customers?${queryString}`);
  },

  async getInvoiceReport(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(authFetch, `/report/invoices?${queryString}`);
  },

  async getInventoryReport(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(authFetch, `/report/inventory?${queryString}`);
  },

  async getFinancialSummary(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(authFetch, `/report/financial-summary?${queryString}`);
  },

  async getProfitLossReport(params = 30) {
    if (typeof params === 'object' && params !== null) {
      const queryString = new URLSearchParams(params).toString();
      return apiCall(authFetch, `/report/profit-loss?${queryString}`);
    }
    return apiCall(authFetch, `/report/profit-loss?days=${params}`);
  }
};

// Profile API functions
export const profileAPI = {
  async getProfile() {
    return apiCall(authFetch, '/profile');
  },
  async updateProfile(data) {
    return apiCall(authFetch, '/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  },
  async updateBusinessInfo(data) {
    return apiCall(authFetch, '/accounts/business-info', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  },
  async getBusinessInfo(accountId) {
    if (accountId) {
      return apiCall(authFetch, `/accounts/business-info/${accountId}`);
    }
    return apiCall(authFetch, '/accounts/business-info');
  },
  async uploadBusinessLogo(accountId, file) {
    const formData = new FormData();
    formData.append('logo', file);

    return apiCall(authFetch, `/accounts/business-info/${accountId}/logo`, {
      method: 'POST',
      body: formData
    });
  },
  async deleteBusinessLogo(accountId) {
    return apiCall(authFetch, `/accounts/business-info/${accountId}/logo`, {
      method: 'DELETE'
    });
  }
};

export async function getRecentActivity() {
  return apiCall(authFetch, '/dashboard/recent-activity');
}

export const subscriptionAPI = {
  async getTrialStatus() {
    return apiCall(authFetch, '/subscription/trial/status');
  }
}; 